// utils/permissionUtils.ts
import { ROLE } from './common';

export interface PermissionState {
    [key: string]: boolean | string;
}

/**
 * Centralized utility to check if a user has Super Admin or Organization Administrator privileges
 * Organization Administrators should be treated as Super Admins for most permission checks
 * Use this function instead of directly checking role === 'Super Admin'
 */
export function isSuperAdminOrOrgAdmin(role: string | undefined | null): boolean {
    return role === ROLE.SUPER_ADMIN || role === ROLE.ORGANIZATION_ADMINISTRATOR;
}

/**
 * Centralized utility to check if a user is specifically an Organization Administrator
 * Use this for features that should ONLY be available to Org Admins (like Settings.tsx)
 */
export function isOrganizationAdministrator(role: string | undefined | null): boolean {
    return role === ROLE.ORGANIZATION_ADMINISTRATOR;
}

/** Any admin role (Super Admin, Organization Administrator, Administrator); mirrors server ADMIN_ROLES. */
export function isAdminRole(role: string | undefined | null): boolean {
    return isSuperAdminOrOrgAdmin(role) || role === ROLE.ADMINISTRATOR;
}

/** Same gate as Position Details Edit Ranges / TargetChart save (editTargets or Super Admin). */
export function canUserEditPositionRanges(
    user?: {
        role?: string;
        permissions?: { editTargets?: boolean };
    } | null,
): boolean {
    if (!user) {
        return false;
    }
    return user.permissions?.editTargets === true || user.role === ROLE.SUPER_ADMIN;
}

/** Aptive advisors and partner resellers share cross-entity access for now. */
export function hasAdvisorLikeAccess(user: { isAdvisor?: boolean; isPartner?: boolean } | null | undefined): boolean {
    return !!user?.isAdvisor || !!user?.isPartner;
}

/** Only god mode users and advisors/partners may assign the Sales Assistant (SDR) role or CRM delegation. */
export function canAssignSalesAssistantRole(
    actor: { isAdvisor?: boolean; isPartner?: boolean } | null | undefined,
    isGodModeUser?: boolean,
): boolean {
    return !!isGodModeUser || hasAdvisorLikeAccess(actor);
}

type AnalyticsPermissionUser = {
    role?: string;
    permissions?: {
        viewAnalytics?: boolean;
        viewAnalyticsSummary?: boolean;
        viewAnalyticsAdoption?: boolean;
    };
    isGodModeUser?: boolean;
    isAdvisor?: boolean;
    isPartner?: boolean;
} | undefined;

function hasAnalyticsRoleBypass(user: AnalyticsPermissionUser): boolean {
    if (!user) {
        return false;
    }
    return (
        !!user.isGodModeUser ||
        hasAdvisorLikeAccess(user) ||
        isSuperAdminOrOrgAdmin(user.role)
    );
}

/** Summary Statistics tab — archetype/profile analytics. */
export function canAccessAnalyticsSummary(user: AnalyticsPermissionUser): boolean {
    if (!user) {
        return false;
    }
    if (hasAnalyticsRoleBypass(user)) {
        return true;
    }
    const perms = user.permissions;
    return perms?.viewAnalyticsSummary === true || perms?.viewAnalytics === true;
}

/** Platform Adoption tab — admin activity and engagement summary. */
export function canAccessAnalyticsAdoption(user: AnalyticsPermissionUser): boolean {
    if (!user) {
        return false;
    }
    if (hasAnalyticsRoleBypass(user)) {
        return true;
    }
    return user.permissions?.viewAnalyticsAdoption === true;
}

/** Analytics nav and page shell — either tab permission grants entry. */
export function canAccessEntityAnalytics(user: AnalyticsPermissionUser): boolean {
    return canAccessAnalyticsSummary(user) || canAccessAnalyticsAdoption(user);
}

/** Whether the global header/mobile search bar should be shown. */
export function canShowUniversalSearch(user: {
    permissions?: { viewOwnProfileOnly?: boolean };
    teamMemberAccess?: boolean;
    teamIds?: string[];
} | null | undefined): boolean {
    if (!user) {
        return false;
    }
    if (!user.permissions?.viewOwnProfileOnly) {
        return true;
    }
    return (
        user.teamMemberAccess === true &&
        Array.isArray(user.teamIds) &&
        user.teamIds.length > 0
    );
}

/**
 * Density score is an Aria Labs feature: godmode makes it available (`densityScoreEnabled`), the org
 * turns Labs and the feature on (`ariaLabsEnabled`, `ariaLabsDensityScoreEnabled`).
 */
export function isOrgDensityScoreOn(
    entity: { densityScoreEnabled?: boolean; ariaLabsEnabled?: boolean; ariaLabsDensityScoreEnabled?: boolean } | null | undefined,
): boolean {
    return !!(entity?.densityScoreEnabled && entity.ariaLabsEnabled && entity.ariaLabsDensityScoreEnabled);
}

/**
 * God Mode always sees Density. Otherwise the org must have it on, and then the Labs audience
 * (`inLabsAudience`, from the server) or anyone with the per-user `canViewDensityScore` grant sees it.
 */
export function canViewDensityScore(
    user: {
        permissions?: { canViewDensityScore?: boolean };
        isGodModeUser?: boolean;
    } | undefined,
    orgDensityScoreOn: boolean,
    inLabsAudience: boolean,
): boolean {
    if (!user) {
        return false;
    }
    if (user.isGodModeUser) {
        return true;
    }
    if (!orgDensityScoreOn) {
        return false;
    }
    return inLabsAudience || user.permissions?.canViewDensityScore === true;
}

/** Who may see the Aria Chat Preview assessment toggle (org/super admins, god mode, advisors, or explicit grant). */
export function userCanConfigureAriaPreviewLinkSettings(
    parsedAuth: { user?: Record<string, unknown> } | null | undefined
): boolean {
    const u = parsedAuth?.user;
    if (!u || typeof u !== 'object') return false;
    const role = typeof u.role === 'string' ? u.role : undefined;
    if (isSuperAdminOrOrgAdmin(role)) return true;
    const perms = u.permissions as Record<string, unknown> | undefined;
    return (
        u.isGodModeUser === true ||
        hasAdvisorLikeAccess(u as { isAdvisor?: boolean; isPartner?: boolean }) ||
        u.canConfigureAriaChatPreviewSettings === true ||
        perms?.canConfigureAriaChatPreviewSettings === true
    );
}
/**
 * Gets the complete Super Admin permissions object for frontend use
 * Super Admins get all permissions EXCEPT viewOwnProfileOnly (which should be false)
 */
export function getSuperAdminPermissions(): PermissionState {
    return {
        // People
        people: true,
        viewPeople: 'All',
        canViewThreeSixty: true,
        canViewDensityScore: true,
        canConfigureAriaChatPreviewSettings: true,

        // Positions & Targets
        positionsTargets: true,
        viewPositionsTargets: 'All',
        editAssignedPeople: true,
        createNewTargets: true,
        editTargets: true,
        archiveTargets: true,
        editTargetNotifications: true,

        // Folders
        folders: true,
        viewFolders: 'All',
        createNewFolders: true,
        editFolders: true,
        archiveFolders: true,
        editFolderNotifications: true,

        // Teams
        teams: true,
        viewTeams: 'All',
        createNewTeams: true,
        editTeams: true,
        archiveTeams: true,

        // Admin
        users: true,
        resetPasswords: true,
        addNewUsers: true,
        removeDeactivateUsers: true,

        // Analytics
        analytics: true,
        viewAnalytics: true,
        viewAnalyticsSummary: true,
        viewAnalyticsAdoption: true,

        // Billing
        billing: true,
        canViewBilling: true,
        canManageBilling: true,

        // Personal Profile
        personalProfile: true,
        viewOwnProfileOnly: false, // Super Admins should NOT be restricted to own profile only
        changeOwnPassword: true,

        // CRM — internal sales pipeline; not granted to portal super/org admins
        crm: false,
        crmViewClosedDeals: false,
        crmAddProspect: false,
        crmEditProspect: false,
        crmChangeStage: false,
        crmCreateTasks: false,
        crmEditTasks: false,
        crmCreateQuote: false,
        crmIntegrations: false,
    };
}

/**
 * Gets initial permissions based on role
 * This centralizes the permission logic for different user roles
 */
/** Administrators must retain People access so they can use People nav and open profiles. */
export function normalizeAdministratorPermissions(role: string, permissions: PermissionState): PermissionState {
    if (role !== ROLE.ADMINISTRATOR) {
        return permissions;
    }
    if (permissions.people === true) {
        return permissions;
    }
    const viewPeople =
        permissions.viewPeople === 'All' || permissions.viewPeople === 'Shared Only'
            ? (permissions.viewPeople as string)
            : 'Shared Only';
    return {
        ...permissions,
        people: true,
        viewPeople,
    };
}

export function getInitialPermissions(role: string): PermissionState {
    if (role === 'Standard User') {
        // Only set Personal Profile permissions, everything else will be undefined/false
        return {
            personalProfile: true,
            viewOwnProfileOnly: true,
            changeOwnPassword: true
        };
    } else if (role === 'Administrator') {
        return {
            // People
            people: true,
            viewPeople: 'Shared Only',
            canViewThreeSixty: false,
            canViewDensityScore: false,
            canConfigureAriaChatPreviewSettings: false,

            // Positions & Targets
            positionsTargets: true,
            viewPositionsTargets: 'Shared Only',
            editAssignedPeople: true,
            createNewTargets: true,
            editTargets: true,
            editTargetNotifications: true,

            // Folders
            folders: true,
            viewFolders: 'Shared Only',
            createNewFolders: true,
            editFolders: true,
            editFolderNotifications: true,

            // Teams
            teams: true,
            viewTeams: 'Shared Only',
            createNewTeams: true,
            editTeams: true,

            // Other sections
            users: false,
            analytics: false,

            // Personal Profile
            personalProfile: true,
            viewOwnProfileOnly: false,
            changeOwnPassword: true,
        };
    } else if (role === ROLE.SALES_ASSISTANT) {
        return {
            people: true,
            viewPeople: 'All',
            folders: true,
            viewFolders: 'Shared Only',
            teams: true,
            viewTeams: 'Shared Only',
            createNewTeams: true,
            personalProfile: true,
            viewOwnProfileOnly: false,
            changeOwnPassword: true,
            crm: false,
            crmViewClosedDeals: false,
            crmAddProspect: false,
            crmEditProspect: false,
            crmChangeStage: false,
            crmCreateTasks: false,
            crmEditTasks: false,
            crmCreateQuote: false,
            crmIntegrations: false,
        };
    } else if (role === 'Super Admin' || role === 'Organization Administrator') {
        return getSuperAdminPermissions();
    }

    return {};
}

/** User may access Clients / CRM surfaces (SDR with crm permission, advisor, or god mode). */
export function canAccessCrmClients(user: {
    isGodModeUser?: boolean;
    isAdvisor?: boolean;
    isPartner?: boolean;
    permissions?: { crm?: boolean };
} | undefined): boolean {
    if (!user) return false;
    if (user.isGodModeUser || hasAdvisorLikeAccess(user)) return true;
    return user.permissions?.crm === true;
} 